import L from 'leaflet';
import { config } from '../config';

const BASE_CHART_LAYER_URL = 'https://tiles-cdn.koordinates.com/services;key=596660e0d02e4dce93c6799002330e1f/tiles/v4/layer={layerId}/EPSG:3857/{z}/{x}/{y}.png';
const LOWER_PRIORITY_CHART_IDS = ['51318', '51276']; // Charts that need a z-index adjustment
const LINZ_ATTRIBUTION = '© <a href="//www.linz.govt.nz/linz-copyright">LINZ CC BY 4.0</a> © <a href="//www.linz.govt.nz/data/linz-data/linz-basemaps/data-attribution">Imagery Basemap contributors</a>';

export const nzChartLayerGroup = L.layerGroup();

export async function updateNzChartLayers(map: L.Map) {
    // Only proceed if the layer group is actually on the map
    if (!map.hasLayer(nzChartLayerGroup)) {
        return;
    }

    try {
        const zoom = map.getZoom();
        const bounds = map.getBounds();
        const extent = {
            north: bounds.getNorth(),
            south: bounds.getSouth(),
            east: bounds.getEast(),
            west: bounds.getWest(),
        };

        // console.log('Fetching charts for:', { zoom, extent });

        const response = await fetch(`${config.serverBaseUrl}/api/map-data`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ zoom, extent }),
        });

        if (!response.ok) {
            console.error("Failed to fetch chart data:", response.statusText);
            return;
        }

        const charts = await response.json();
        
        if (!Array.isArray(charts)) {
            console.error("Invalid response from API:", charts);
            return;
        }

        // console.log('Received charts:', charts.map((c: any) => c.readable || c.layerId));

        maintainNzChartOverlayGroup(charts, map);

    } catch (error) {
        console.error("Error updating map layers:", error);
    }
}

function maintainNzChartOverlayGroup(charts: any[], map: L.Map) {
    removeRedundantLayers(charts);

    // Sort by scale (descending) so smaller scale (larger area) charts are processed first.
    // This sorting is still useful to ensure a consistent order when adding layers,
    // even if zIndex is explicitly set.
    charts.sort((a, b) => b.scale - a.scale).forEach(chart => {
        const existsInLayer = nzChartLayerGroup.getLayers().some((layer: any) => layer.options.layerId === chart.layerId);
        if (!existsInLayer) {
            // console.log('Adding chart layer:', chart.layerId);
            const layer = generateMapLayer(chart);
            layer.addTo(nzChartLayerGroup);
        }
    });

    // Dispatch event to notify UI that charts have changed
    const event = new CustomEvent('nz-charts-updated');
    map.getContainer().dispatchEvent(event);
}

function removeRedundantLayers(incomingCharts: any[]) {
    nzChartLayerGroup.eachLayer(function (layer: any) {
        const layerId = layer.options.layerId;
        // Keep persistent layers or those in the new list
        const existsInIncoming = incomingCharts.some(chart => chart.layerId === layerId);
        if (!existsInIncoming) {
            // console.log('Removing chart layer:', layerId);
            nzChartLayerGroup.removeLayer(layer);
        }
    });
}

function generateMapLayer(chart: any) {
    const url = BASE_CHART_LAYER_URL.replace('{layerId}', chart.layerId);

    // Correct zIndex calculation: Higher detail (smaller scale number) gets a higher zIndex.
    // Use a range that is manageable and allows for fine-tuning.
    // Example: scale 100000 -> zIndex 990, scale 10000 -> zIndex 999
    let zIndex = 1000 - Math.floor(chart.scale / 10000); 
    
    // Apply the hard-coded fiddle for the specific chart
    if (LOWER_PRIORITY_CHART_IDS.includes(chart.layerId.toString())) {
        // This chart needs to be rendered just below another chart that covers part of its area.
        zIndex--;
    }
    
    return L.tileLayer(url, {
        maxZoom: 18,
        attribution: LINZ_ATTRIBUTION,
        // @ts-ignore - custom option for tracking
        layerId: chart.layerId,
        chartName: chart.readable || `Chart ${chart.layerId}`, // Store readable name
        zIndex: zIndex // Use the calculated zIndex
    });
}
